import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useCalendarLoad, type CalendarDay } from "../api/calendar.js";
import { ErrorState, Loading } from "../components/Loading.js";
import { cn } from "../lib/cn.js";

// Heatmap-сетка по неделям. Цвет тем темнее, чем больше items парсится в этот день.

const WEEK_LABELS = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

function startOfMonth(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}
function endOfMonth(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0, 23, 59, 59, 999));
}
function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}
// Сдвиг от начала недели (Пн=0, Вс=6) для UTC-дня date.
function weekDayMonFirst(d: Date): number {
  const dow = d.getUTCDay(); // 0=Sun..6=Sat
  return (dow + 6) % 7; // → 0=Mon..6=Sun
}

export function CalendarPage() {
  const [cursor, setCursor] = useState<Date>(() => startOfMonth(new Date()));
  const from = startOfMonth(cursor);
  const to = endOfMonth(cursor);
  const query = useCalendarLoad(from.toISOString(), to.toISOString());

  const monthLabel = cursor.toLocaleDateString("ru-RU", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });

  const weeks = useMemo(() => buildWeeksGrid(from, to, query.data?.days ?? []), [
    from,
    to,
    query.data,
  ]);

  // Максимум для нормализации цвета.
  const maxCount = useMemo(() => {
    let m = 0;
    for (const d of query.data?.days ?? []) {
      if (d.count > m) m = d.count;
    }
    return m;
  }, [query.data]);

  const totalForMonth = useMemo(() => {
    return (query.data?.days ?? []).reduce((s, d) => s + d.count, 0);
  }, [query.data]);

  const itemMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const i of query.data?.items ?? []) m.set(i.id, i.displayName);
    return m;
  }, [query.data]);

  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);

  return (
    <div className="space-y-3 p-3">
      <h1 className="text-lg font-bold">Календарь парсинга</h1>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setCursor((c) => new Date(Date.UTC(c.getUTCFullYear(), c.getUTCMonth() - 1, 1)))}
          className="rounded-lg bg-bg-secondary px-3 py-1.5 text-sm"
        >
          ←
        </button>
        <div className="font-semibold capitalize">{monthLabel}</div>
        <button
          type="button"
          onClick={() => setCursor((c) => new Date(Date.UTC(c.getUTCFullYear(), c.getUTCMonth() + 1, 1)))}
          className="rounded-lg bg-bg-secondary px-3 py-1.5 text-sm"
        >
          →
        </button>
      </div>

      {query.isLoading && <Loading />}
      {query.isError && <ErrorState error={query.error} />}
      {query.isSuccess && (
        <>
          <div className="text-xs text-hint">
            Всего запусков за месяц: <b className="text-text">{totalForMonth}</b>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-hint">
            {WEEK_LABELS.map((l) => (
              <div key={l}>{l}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {weeks.flat().map((cell, idx) => {
              if (!cell) return <div key={idx} className="aspect-square" />;
              const intensity = maxCount > 0 ? cell.count / maxCount : 0;
              const isSelected = selectedDay?.date === cell.date;
              return (
                <button
                  key={cell.date}
                  type="button"
                  onClick={() => setSelectedDay(cell)}
                  className={cn(
                    "relative aspect-square rounded-md border text-center text-xs transition",
                    isSelected ? "border-button" : "border-transparent",
                  )}
                  style={{
                    backgroundColor: heatBg(intensity),
                  }}
                >
                  <span className="absolute left-1 top-0.5 text-[10px] opacity-70">
                    {Number.parseInt(cell.date.slice(8), 10)}
                  </span>
                  {cell.count > 0 && (
                    <span className="absolute inset-0 flex items-center justify-center text-base font-bold">
                      {cell.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {selectedDay && (
            <div className="rounded-xl bg-bg-secondary p-3">
              <div className="mb-2 text-sm">
                <b>{selectedDay.date}</b> — запусков: {selectedDay.count}
              </div>
              {selectedDay.itemIds.length === 0 && (
                <div className="text-xs text-hint">Нет запланированных item'ов</div>
              )}
              <div className="space-y-1">
                {selectedDay.itemIds.map((id) => (
                  <Link
                    key={id}
                    to={`/items/${id}`}
                    className="block truncate rounded bg-bg px-2 py-1 text-xs"
                  >
                    {itemMap.get(id) ?? id}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Делит окно на недели с лидирующими/трейлинг пробелами.
function buildWeeksGrid(
  from: Date,
  to: Date,
  days: CalendarDay[],
): Array<Array<CalendarDay | null>> {
  const map = new Map<string, CalendarDay>();
  for (const d of days) map.set(d.date, d);

  const result: Array<Array<CalendarDay | null>> = [];
  // Стартуем с понедельника содержащего from.
  const firstWeekStart = new Date(from);
  firstWeekStart.setUTCDate(firstWeekStart.getUTCDate() - weekDayMonFirst(from));
  let cursor = new Date(firstWeekStart);

  while (cursor <= to) {
    const week: Array<CalendarDay | null> = [];
    for (let i = 0; i < 7; i++) {
      if (cursor < from || cursor > to) {
        week.push(null);
      } else {
        const key = isoDate(cursor);
        week.push(
          map.get(key) ?? { date: key, count: 0, itemIds: [] },
        );
      }
      cursor.setUTCDate(cursor.getUTCDate() + 1);
    }
    result.push(week);
  }
  return result;
}

// Палитра «прохладный → горячий» через css var TG-button (синий) с прозрачностью.
function heatBg(intensity: number): string {
  if (intensity <= 0) return "var(--tg-bg-secondary)";
  // 0..1 → 0.15..0.85 alpha над button-цветом.
  const alpha = 0.15 + intensity * 0.7;
  return `color-mix(in srgb, var(--tg-button) ${Math.round(alpha * 100)}%, transparent)`;
}
